import React from "react"

import { useQueryClient } from "@tanstack/react-query"
import { usePathname, useRouter } from "expo-router"
import Toast from "react-native-toast-message"
import { Tevent, TMessage } from "types/transcript"

import { useAuth } from "src/context/AuthContext"
import { useMediaContext } from "src/context/MediaContextProvider"
import { useSentimentModal } from "src/context/SentimentModalContext"
import { getMediaTypeFromEvent } from "src/utils/mediaTypes"
import { openWebsite } from "src/utils/openWebsite"
import { parseTimestampToSeconds } from "src/utils/time"
import { getYouTubeVideoId, isYouTubeVideo } from "src/utils/youtubeUtils"

export const useChatMessageEvents = () => {
  const { apiHost } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const queryClient = useQueryClient()
  const { sentimentModalRef } = useSentimentModal()
  const { getLastPosition, consumeSuppressNextMediaOpen } = useMediaContext()

  const [events, setEvents] = React.useState<Tevent[]>([])

  const executeMessageEvents = React.useCallback(() => {
    const eventsToExec = [...events]
    setEvents([])

    // A single batch can contain multiple media-opening events (events are
    // accumulated across messages in ChatContext before they're drained here).
    // Navigating once per event would `router.push` several player screens in
    // the same tick — `pathname` doesn't update mid-loop, so each event sees the
    // same "home" location and pushes. The stacked-but-unfocused players stay
    // mounted and autoplay, producing audible playback from a video that isn't
    // on screen. Instead we record only the LAST media-opening navigation and
    // run it once after the loop, so the user lands on a single player.
    let pendingMediaNavigation: (() => void) | null = null

    // Media events automatically open their respective viewers/players
    // Media is also accessible via thumbnails in chat bubbles
    for (const event of eventsToExec) {
      switch (event.event_type) {
        case "ShowTDLPanel":
          // TODO: Navigate to content-library route if needed
          break
        case "HideTDLPanel":
          // No-op - panels are now handled via navigation
          break
        case "ExpandTDLSection":
          // TodoList watches for this event
          break
        case "TDLChanged":
          queryClient.invalidateQueries({ queryKey: ["todos"] })
          break
        case "PlayVideo": {
          // Skip when the user already launched the player directly from the content library.
          if (consumeSuppressNextMediaOpen()) break
          const videoMediaType = getMediaTypeFromEvent(event, apiHost)
          if (videoMediaType?.type === "video") {
            // For YouTube videos, use the full URL as the resource key
            // For other videos, prefer resourceId or fall back to URL
            const isYouTube = isYouTubeVideo(videoMediaType.url)
            const resourceKey = isYouTube
              ? videoMediaType.url
              : videoMediaType.id || videoMediaType.url || ""

            // Priority: 1) Backend offset 2) Saved position 3) Start from beginning
            const savedPosition = getLastPosition(resourceKey)
            const startTime = videoMediaType.offset
              ? String(parseTimestampToSeconds(videoMediaType.offset))
              : savedPosition?.offset
                ? String(parseTimestampToSeconds(savedPosition.offset))
                : "0"

            // Both chat and the Content Library are home screens — push the player on
            // top so closing it returns the user to whichever screen they came from.
            // Replace only when already inside another player/overlay.
            const isAtHomeScreen = pathname === "/" || pathname === "/content-library"
            const navigate = isAtHomeScreen ? router.push : router.replace

            pendingMediaNavigation = () => {
              if (isYouTube) {
                navigate({
                  pathname: "/youtube-player",
                  params: {
                    videoId: getYouTubeVideoId(videoMediaType.url || ""),
                    startTime,
                    resourceId: videoMediaType.id,
                  },
                } as any)
              } else {
                navigate({
                  pathname: "/video-player",
                  params: {
                    videoUrl: videoMediaType.hlsUrl || videoMediaType.url || "",
                    resourceId: videoMediaType.id,
                    title: videoMediaType.title,
                    taskId: videoMediaType.taskId,
                    totalLength: videoMediaType.totalLength,
                    startTime,
                    ...(videoMediaType.thumbnail ? { thumbnailUrl: videoMediaType.thumbnail } : {}),
                  },
                } as any)
              }
            }
          }
          break
        }
        case "PlayAudio": {
          const audioMediaType = getMediaTypeFromEvent(event, apiHost)
          if (audioMediaType?.type === "audio") {
            // Priority: 1) Backend offset 2) Saved position 3) Start from beginning
            const resourceKey = audioMediaType.id || audioMediaType.url || ""
            const savedPosition = getLastPosition(resourceKey)
            const startTime = audioMediaType.offset
              ? String(parseTimestampToSeconds(audioMediaType.offset))
              : savedPosition?.offset
                ? String(parseTimestampToSeconds(savedPosition.offset))
                : "0"

            const isAtHomeScreen = pathname === "/" || pathname === "/content-library"
            const navigate = isAtHomeScreen ? router.push : router.replace

            pendingMediaNavigation = () =>
              navigate({
                pathname: "/audio-player",
                params: {
                  audioUrl: audioMediaType.url,
                  audioTitle: audioMediaType.title,
                  startTime,
                  autoplay: audioMediaType.autoplay ? "true" : "false",
                  resourceId: audioMediaType.id,
                  taskId: audioMediaType.taskId,
                  totalLength: audioMediaType.totalLength,
                },
              } as any)
          }
          break
        }
        case "OpenResource": {
          // Skip when the user already launched the player directly from the content library.
          if (consumeSuppressNextMediaOpen()) break
          const resourceMediaType = getMediaTypeFromEvent(event, apiHost)
          if (resourceMediaType?.type === "resource") {
            const isAtHomeScreen = pathname === "/" || pathname === "/content-library"
            const navigate = isAtHomeScreen ? router.push : router.replace

            pendingMediaNavigation = () => {
              if (resourceMediaType.resourceType === "youtube") {
                // Route YouTube resources to the dedicated YouTube player
                const savedPosition = getLastPosition(resourceMediaType.id)
                // Prefer backend-provided offset; only fall back to locally-saved position if none provided
                const resumeOffset = resourceMediaType.offset ?? savedPosition?.offset
                const startTime = resumeOffset ? String(parseTimestampToSeconds(resumeOffset)) : "0"
                navigate({
                  pathname: "/youtube-player",
                  params: {
                    videoId: getYouTubeVideoId(resourceMediaType.url),
                    startTime,
                    resourceId: resourceMediaType.id,
                    title: resourceMediaType.title,
                  },
                } as any)
              } else {
                const isPdf = resourceMediaType.resourceType === "pdf"
                if (isPdf) {
                  // For PDFs, offset is a page number (integer), not a timestamp.
                  // Prefer backend-provided offset; fall back to locally-saved page position.
                  const savedPosition = getLastPosition(resourceMediaType.id)
                  const resumePage = resourceMediaType.offset ?? savedPosition?.offset
                  navigate({
                    pathname: "/pdf-viewer",
                    params: {
                      url: resourceMediaType.url,
                      title: resourceMediaType.title,
                      resourceId: resourceMediaType.id,
                      taskId: resourceMediaType.taskId,
                      ...(resumePage ? { startPage: resumePage } : {}),
                    },
                  } as any)
                } else {
                  navigate({
                    pathname: "/file-viewer",
                    params: {
                      url: resourceMediaType.url,
                      title: resourceMediaType.title,
                      resourceId: resourceMediaType.id,
                      taskId: resourceMediaType.taskId,
                    },
                  } as any)
                }
              }
            }
          }
          break
        }
        case "HideVideoPanel":
          // No-op - panels are now handled via navigation
          break
        case "ShowImage": {
          const imageMediaType = getMediaTypeFromEvent(event, apiHost)
          if (imageMediaType?.type === "image" && !event.event_args?.intro_image) {
            const isAtHomeScreen = pathname === "/" || pathname === "/content-library"
            const navigate = isAtHomeScreen ? router.push : router.replace

            pendingMediaNavigation = () =>
              navigate({
                pathname: "/image-viewer",
                params: {
                  imageUrl: imageMediaType.url,
                },
              } as any)
          }
          break
        }
        case "HideImage":
          // No-op - panels are now handled via navigation
          break
        case "ShowWebBrowserPanel": {
          // Skip when the user already launched the browser directly from the content library.
          if (consumeSuppressNextMediaOpen()) break
          const websiteMediaType = getMediaTypeFromEvent(event, apiHost)
          if (websiteMediaType?.type === "website") {
            const url = websiteMediaType.url || ""
            const isAtHomeScreen = pathname === "/" || pathname === "/content-library"
            const navigate = isAtHomeScreen ? router.push : router.replace

            // Open YouTube URLs in youtube-player (match thumbnail behavior)
            pendingMediaNavigation = () => {
              if (isYouTubeVideo(url)) {
                const resourceKey = url
                const savedPosition = getLastPosition(resourceKey)
                const startTime = savedPosition?.offset
                  ? String(parseTimestampToSeconds(savedPosition.offset))
                  : "0"
                navigate({
                  pathname: "/youtube-player",
                  params: {
                    videoId: getYouTubeVideoId(url),
                    startTime,
                    resourceId: websiteMediaType.id,
                  },
                } as any)
              } else {
                openWebsite(url, navigate)
              }
            }
          }
          break
        }
        case "HideWebBrowserPanel":
          // No-op - panels are now handled via navigation
          break
        case "ShowToast":
          Toast.show({
            type: "info",
            text1: event.event_args?.message || "New Message",
          })
          break
        case "UserPressedSmiley":
          // Not sure what this is supposed to do
          break
        case "ShowWellnessBar":
          sentimentModalRef.current?.present()
          break
        case "SwitchCM":
          // FIXME: SwitchCM is deprecated - sidekick switches are now handled via server-sent start_conversation events
          console.warn(
            "[useChatMessageEvents] Received deprecated SwitchCM event. Sidekick switches should use start_conversation events.",
            event.event_args,
          )
          break
        case "ShowCrisisLine":
          // TODO: Implement crisis line as a SidekickView-based route if needed
          break
      }
    }

    // Run only the last media-opening navigation from this batch. This prevents
    // stacking multiple player screens (and the resulting hidden-but-audible
    // playback) when several media events arrive together.
    pendingMediaNavigation?.()
  }, [
    events,
    queryClient,
    sentimentModalRef,
    apiHost,
    router,
    pathname,
    getLastPosition,
    consumeSuppressNextMediaOpen,
  ])

  React.useEffect(() => {
    if (events.length > 0) {
      executeMessageEvents()
    }
  }, [events, executeMessageEvents])

  const onChatMessageWithEvents = React.useCallback(
    (message: TMessage) => {
      const messageEvents = message.events as Tevent[]
      if (messageEvents?.length > 0) {
        setEvents((prev) => [...prev, ...messageEvents])
      }
    },
    [setEvents],
  )

  return { onChatMessageWithEvents, executeMessageEvents }
}
